/**
 * Meta's in-app browsers, from the user agent. Facebook, Instagram, and
 * Messenger open links in their own webview, and Google refuses OAuth in a
 * webview (403 disallowed_useragent), so there the sign-in screens put
 * Facebook first and say how to reach a real browser for Google. Pure, so
 * the user agents are unit-tested.
 */
import type { SignInProvider } from "./sign-in-error.ts";

export type MetaApp = "facebook" | "instagram" | "messenger";

/**
 * The app's name, as the sign-in hint says it. Messenger on iPhone opens
 * links in the Facebook app's browser, with the same [FBAN/FBIOS…] user
 * agent (seen on a real iPhone, 2026-10-18), so that marker names both.
 */
export const META_APP_NAMES: Record<MetaApp, string> = {
  facebook: "Facebook or Messenger",
  instagram: "Instagram",
  messenger: "Messenger",
};

/** Which of Meta's apps this page is open inside, or null for a real browser. */
export function metaInAppBrowser(userAgent: string): MetaApp | null {
  // Instagram's own token, "Instagram 337.0.3.23.54" (a space, not a slash);
  // its user agent carries no FB* markers.
  if (/\bInstagram \d/.test(userAgent)) return "instagram";
  // Messenger's own markers: FB_IAB/Orca-Android, and FBAN/Messenger…ForiOS
  // in older iPhone versions.
  if (/FBAN\/Messenger|Orca-Android/.test(userAgent)) return "messenger";
  // Facebook is [FBAN/FBIOS;FBAV/…] on iPhone (Messenger's links too) and
  // [FB_IAB/FB4A;FBAV/…] on Android.
  if (/FBAN\/|FBAV\/|FB_IAB\//.test(userAgent)) return "facebook";
  return null;
}

/**
 * The sign-in buttons, top first. Google leads in a real browser. Inside
 * Meta's apps Facebook leads, because Google refuses to sign in there; the
 * Google button stays for a parent who opens the page in a browser.
 */
export function signInOrder(facebookOn: boolean, app: MetaApp | null): SignInProvider[] {
  if (!facebookOn) return ["google"];
  return app ? ["facebook", "google"] : ["google", "facebook"];
}
